import { RefreshCw } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { pickCss } from '@/shared/lib/css-utils';
import { titleCaseRegionTitle } from '../model/region-layout';
import {
  initialTagBar,
  pinSelected,
  refreshTagBar,
  tagsThatFit,
} from '../model/v3-tag-bar';
import css from './planner-v3-tag-bar.module.scss';

const chipCss = pickCss(css, css.chip);

/** The row's leading tags that fit; every one when they all do. */
const row = (
  shown: readonly string[],
  fit: number,
  tags: readonly string[]
): string[] => (tags.length === 0 ? [] : shown.slice(0, Math.max(1, fit)));

type PlannerV3TagBarProps = {
  /** Every tag of the chosen type. */
  tags: readonly string[];
  selected: readonly string[];
  onToggle: (tag: string) => void;
};

/**
 * Tags above the results, on one line: as many of the row's eight as fit
 * beside **More** (another set), the selected ones always first (as on the
 * CMS storefront).
 */
export const PlannerV3TagBar = ({
  tags,
  selected,
  onToggle,
}: PlannerV3TagBarProps) => {
  const tagsKey = tags.join('|');
  const tagsCount = tags.length;
  const [shown, setShown] = useState(() => initialTagBar(tags, selected));
  const [fit, setFit] = useState(shown.length);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);

  // Another type brings its own tags.
  useEffect(() => {
    setShown(initialTagBar(tags, selected));
  }, [tagsKey]);

  // A tag selected elsewhere (the URL, a brief) is never hidden.
  useEffect(() => {
    setShown((current) => pinSelected(current, selected));
  }, [selected]);

  // Measure the chips off-screen against the row's width, on every resize.
  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return;
    const update = () => {
      const chips = [...measure.querySelectorAll<HTMLElement>('[data-chip]')];
      const more = measure.querySelector<HTMLElement>('[data-more]');
      const fitWith = (moreWidth: number) =>
        tagsThatFit({
          chipWidths: chips.map((chip) => chip.offsetWidth),
          moreWidth,
          available: row.clientWidth,
          gap: Number.parseFloat(getComputedStyle(row).columnGap) || 0,
        });
      // No More to make room for when every tag is in the row and fits.
      const all = fitWith(0);
      setFit(
        all === chips.length && tagsCount <= chips.length
          ? all
          : fitWith(more?.offsetWidth ?? 0)
      );
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(row);
    return () => observer.disconnect();
  }, [shown, tagsCount]);

  if (tags.length === 0) return null;

  const visible = row(shown, fit, tags);
  const hasMore = tags.length > visible.length;
  const chip = (tag: string, measured = false) => {
    const isOn = selected.includes(tag);
    return (
      <button
        aria-pressed={measured ? undefined : isOn}
        className={chipCss({ isActive: isOn })}
        data-chip={measured ? '' : undefined}
        key={tag}
        onClick={measured ? undefined : () => onToggle(tag)}
        tabIndex={measured ? -1 : undefined}
        type="button"
      >
        {titleCaseRegionTitle(tag)}
      </button>
    );
  };
  const more = (measured = false) => (
    <button
      aria-label={measured ? undefined : 'More tags'}
      className={css.more}
      data-more={measured ? '' : undefined}
      onClick={
        measured
          ? undefined
          : () => setShown(refreshTagBar(tags, visible, selected))
      }
      tabIndex={measured ? -1 : undefined}
      title={measured ? undefined : 'Show other tags — selected ones stay'}
      type="button"
    >
      <RefreshCw aria-hidden="true" size={12} strokeWidth={2.4} />
      More
    </button>
  );

  return (
    <fieldset aria-label="Tags" className={css.root}>
      <legend className={css.title}>Choose the mood:</legend>
      <div className={css.row} ref={rowRef}>
        {visible.map((tag) => chip(tag))}
        {hasMore ? more() : null}
      </div>
      <div aria-hidden="true" className={css.measure} ref={measureRef}>
        {shown.map((tag) => chip(tag, true))}
        {more(true)}
      </div>
    </fieldset>
  );
};
